import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Divider,
  Menu,
  MenuItem,
  Tooltip,
  ListItemText,
  ListItemIcon,
} from "@mui/material";
import { Check as CheckIcon, ExpandMore as ExpandMoreIcon } from "@mui/icons-material";
import { useState, type MouseEvent } from "react";
import { formatDocumentCategory } from "../../utils/documentUtils";
import { HeaderTriggerButton } from "./HeaderTriggerButton";
import type { DocumentClassification } from "../../hooks/useDocumentClassification";

// A pending change captured between dropdown selection and reprocess
// confirmation. `value: null` means "clear an existing override"; a string
// means "set this as the new override." Stored in state so the dialog can
// describe the about-to-happen transition without re-querying the chip's
// other props.
type PendingChange = {
  value: string | null;
  // For the dialog body — what the reviewer is changing FROM. Lets us
  // render "X → Y" cleanly.
  fromLabel: string;
};

interface ClassificationChipProps {
  classification: DocumentClassification | null;
  isLoading: boolean;
  isSaving: boolean;
  /**
   * True while the post-override reprocess is running on the worker.
   * Chip swaps in a "Reprocessing…" label + spinner during this state and
   * disables the menu so the reviewer can't kick off another reprocess
   * mid-flight.
   */
  isReprocessing: boolean;
  error: string | null;
  /**
   * "chip" (default) renders the outlined toolbar chip. "text" renders the
   * review header's label-less text button (#7431, artboard 1a) — same menu,
   * same confirm, different trigger — so the four header cells look alike.
   */
  variant?: "chip" | "text";
  /** Persist a new override; pass null to clear an existing override. */
  onOverride: (value: string | null) => Promise<void>;
}

/**
 * Compact replacement for the standalone ClassificationBanner (AB#4363).
 * Lives in the document review toolbar next to the deductible / projection
 * chips, matching the visual rhythm of the rest of the page header.
 *
 * Behavior:
 *   - Color signals state at a glance: `success` = auto-classified value
 *     is in effect, `info` = reviewer override is in effect, `default` =
 *     classification is null (still processing) or a load error occurred.
 *   - Click opens a Menu listing every available_option from the server.
 *     Selecting the auto-classified value clears the override (sends null);
 *     selecting any other value sets it as the new override.
 *   - Tooltip on hover surfaces the full context (auto vs override vs error)
 *     so the reviewer doesn't have to open the menu to compare.
 *
 * Self-hides when the document has no transformation yet — the toolbar
 * already contains the records-extracted count, which doubles as the
 * "still processing" signal.
 */
export const ClassificationChip = ({
  classification,
  isLoading,
  isSaving,
  isReprocessing,
  error,
  onOverride,
  variant = "chip",
}: ClassificationChipProps) => {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const isMenuOpen = Boolean(anchorEl);
  // Two-step interaction: a dropdown pick stages a PendingChange; the
  // user must explicitly confirm in the dialog before we POST. Reviewers
  // were getting surprised by silent reprocess, so the dialog is the
  // explicit "yes, I know this is going to reprocess the document" gate.
  const [pendingChange, setPendingChange] = useState<PendingChange | null>(null);

  // Empty state: nothing to render. Matches the banner's behavior — the
  // banner-collecting feedback was specifically about reducing visual noise
  // before the reviewer sees data, so the chip stays silent in this state.
  if (!classification && !error) return null;

  // Hard error from the GET — render an outlined chip with the error in
  // the tooltip so the reviewer knows something's off without blocking them.
  // Sized to match the rest of the toolbar chips (height 24, 0.75rem label).
  if (error || !classification) {
    return (
      <Tooltip title={`Couldn't load classification: ${error ?? "unknown error"}. Override unavailable.`}>
        <Chip
          size="small"
          label="Classification unavailable"
          variant="outlined"
          sx={{ height: 24, "& .MuiChip-label": { px: 1, fontSize: "0.75rem" } }}
        />
      </Tooltip>
    );
  }

  // Reprocess in flight: swap in a "Reprocessing…" chip with a spinner.
  // Takes precedence over the normal classification rendering so the
  // reviewer gets unambiguous feedback that the system is doing work, and
  // can't accidentally fire another reprocess by clicking. The chip's
  // label is updated when the reprocess settles (the parent hook clears
  // isReprocessing, classification refetches, normal chip returns).
  if (isReprocessing) {
    return (
      <Tooltip title="Reprocessing the document with the new classification — the page will refresh automatically when it's done. This usually takes 10-30 seconds.">
        <Chip
          size="small"
          icon={<CircularProgress size={12} thickness={6} sx={{ ml: 0.5 }} />}
          label="Reprocessing…"
          variant="outlined"
          sx={{
            height: 24,
            "& .MuiChip-label": { px: 1, fontSize: "0.75rem" },
            "& .MuiChip-icon": { mr: -0.25, ml: 0.5 },
          }}
        />
      </Tooltip>
    );
  }

  const { auto_classified, override, effective, available_options, existing_template_options } = classification;
  // Treat an override as active only when it actually differs from the
  // auto-classified value. After a reviewer override propagates through
  // reprocess, the new template's source_document_type matches the
  // override — at that point the override row still exists but it's
  // functionally a no-op, and showing "overridden" here would be
  // misleading. Once they agree, this is just the normal classified state.
  const isOverridden = override !== null && override !== undefined && override !== auto_classified;
  // Set form for fast lookup when rendering the [Existing] pill on each
  // menu item.
  const existingTemplateSet = new Set(existing_template_options ?? []);
  // Backend returns null for all three when the document hasn't produced a
  // transformation. Stay silent — the records-extracted toolbar text is
  // already the empty-state signal.
  // The header's text trigger stays usable with no value as long as there is
  // something to pick (#7431 AC1); the toolbar chip keeps the silent empty state.
  const canOffer = variant === "text" && (available_options?.length ?? 0) > 0;
  if (effective === null && auto_classified === null && !canOffer) return null;

  const displayLabel = effective
    ? formatDocumentCategory(effective)
    : variant === "text"
      ? "Uncategorized"
      : "Unclassified";
  const tooltipContent = isOverridden
    ? `Reviewer override: ${formatDocumentCategory(override ?? "")}. Auto-classified: ${formatDocumentCategory(auto_classified ?? "—")}. Click to change. Changing the classification triggers a reprocess so the document is treated as the new shape; the page will update automatically when it's done.`
    : `Auto-classified as ${formatDocumentCategory(effective ?? "")}. Click to override. Changing the classification triggers a reprocess so the document is treated as the new shape; the page will update automatically when it's done.`;

  const handleOpen = (event: MouseEvent<HTMLDivElement>) => setAnchorEl(event.currentTarget);
  const handleClose = () => setAnchorEl(null);

  const handleSelect = (value: string) => {
    handleClose();
    // Skip the dialog when the click is a no-op against current state —
    // the reviewer clicked the value that's already effective. Saves a
    // pointless confirmation and a pointless reprocess.
    if (value === effective) {
      return;
    }
    // Selecting the auto-classified value clears the override (sends
    // null over the wire) — the reviewer is reverting to whatever the
    // classifier picked. Stage the pending change for the dialog instead
    // of POSTing immediately.
    if (value === auto_classified) {
      setPendingChange({ value: null, fromLabel: formatDocumentCategory(effective ?? "") });
      return;
    }
    setPendingChange({ value, fromLabel: formatDocumentCategory(effective ?? "") });
  };

  const handleClearOverride = () => {
    handleClose();
    setPendingChange({ value: null, fromLabel: formatDocumentCategory(effective ?? "") });
  };

  const handleConfirmPending = () => {
    if (pendingChange === null) return;
    void onOverride(pendingChange.value);
    setPendingChange(null);
  };

  const handleCancelPending = () => {
    setPendingChange(null);
  };

  const trigger =
    variant === "text" ? (
      <HeaderTriggerButton
        onClick={(e) => setAnchorEl(e.currentTarget)}
        disabled={isLoading || isSaving}
        aria-label="Change category"
        aria-haspopup="menu"
        aria-expanded={isMenuOpen}
      >
        {displayLabel}
      </HeaderTriggerButton>
    ) : null;

  return (
    <>
      {trigger ? (
        <Tooltip title={tooltipContent}>
          <span>{trigger}</span>
        </Tooltip>
      ) : (
        <Tooltip title={tooltipContent}>
          {/* span wrapper required by MUI when the inner element can be
            disabled (Chip with onClick can't be disabled inline cleanly,
            but the wrapper also makes the Tooltip stable across clicks).
            Sizing intentionally matches DeductibleContextChips and
            ProjectionWarningChips — same height (24), same label font
            size (0.75rem), same outlined variant — so the toolbar reads
            as one consistent row of metadata chips. The override state
            isn't communicated by chip color; the label, tooltip, and
            menu checkmark all carry that signal. */}
          <span>
            <Chip
              size="small"
              label={displayLabel}
              variant="outlined"
              onClick={handleOpen}
              // onDelete + deleteIcon is MUI's idiomatic way to attach a
              // right-side icon to a Chip. We map both onClick and onDelete
              // to the same handler — clicking either the body or the
              // chevron opens the menu. Semantic note: it's named "delete"
              // in MUI for historical reasons but is the standard pattern
              // for right-side chip icons that aren't destructive.
              onDelete={handleOpen}
              deleteIcon={<ExpandMoreIcon aria-label="Open classification menu" sx={{ fontSize: "1rem !important" }} />}
              disabled={isLoading || isSaving}
              // aria-label is the chip's accessible name — kept stable across
              // renders so tests + screen readers can target it predictably.
              aria-label="Document classification"
              sx={{
                height: 24,
                cursor: "pointer",
                "& .MuiChip-label": { px: 1, fontSize: "0.75rem" },
                // Pull the chevron a hair tighter so the chip footprint
                // matches the deductible/warning chips' compactness.
                "& .MuiChip-deleteIcon": { mr: 0.5, ml: -0.25 },
              }}
            />
          </span>
        </Tooltip>
      )}
      <Menu anchorEl={anchorEl} open={isMenuOpen} onClose={handleClose}>
        {/* When an override is active, surface an explicit "Clear override"
            affordance at the top of the menu. Reviewers shouldn't have to
            remember which value was originally auto-classified to revert —
            this is the discoverable path. Sends null over the wire (same
            handler as picking the auto-classified value, just an explicit
            entry point). Hidden when there's no override to clear. */}
        {isOverridden && (
          <MenuItem onClick={handleClearOverride}>
            <ListItemIcon sx={{ minWidth: 28, visibility: "hidden" }}>
              {/* Spacer so the label aligns with the option rows below. */}
              <CheckIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText
              primary="Clear override"
              primaryTypographyProps={{ sx: { fontStyle: "italic", color: "text.secondary" } }}
            />
          </MenuItem>
        )}
        {isOverridden && <Divider sx={{ my: 0.5 }} />}
        {available_options.map((opt) => {
          const isSelected = opt === effective;
          const hasExistingTemplate = existingTemplateSet.has(opt);
          return (
            <MenuItem key={opt} selected={isSelected} onClick={() => handleSelect(opt)}>
              <ListItemIcon sx={{ minWidth: 28, visibility: isSelected ? "visible" : "hidden" }}>
                <CheckIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText primary={formatDocumentCategory(opt)} />
              {/* "Existing" pill — flags shapes that already have an active
                  template for this (partner, vendor). Picking one of these
                  reuses the template on next reprocess; picking a value
                  without the pill triggers fresh AI inference. Subtle by
                  design: it's a hint, not a directive. The tooltip
                  spells out what "existing" means so reviewers don't have
                  to infer it — a pill alone is too cryptic for users who
                  don't already understand the template-reuse model. */}
              {hasExistingTemplate && (
                <Tooltip
                  title="A template for this shape already exists for this client/vendor combination. Picking it will reuse that template's field mappings; values without this tag will run fresh AI inference."
                  placement="right"
                  arrow
                >
                  <Box
                    component="span"
                    sx={{
                      ml: 2,
                      px: 0.75,
                      py: 0.125,
                      fontSize: "0.6875rem",
                      lineHeight: 1.4,
                      borderRadius: 0.5,
                      bgcolor: "action.selected",
                      color: "text.secondary",
                      flexShrink: 0,
                      cursor: "help",
                    }}
                    aria-label="Existing template available"
                  >
                    Existing
                  </Box>
                </Tooltip>
              )}
            </MenuItem>
          );
        })}
      </Menu>

      {/* Reprocess confirmation dialog. Opens whenever the reviewer picks
          a different value (or "Clear override"); they must explicitly
          confirm before we POST. The body explains what's about to happen
          so reviewers aren't surprised by the page transitioning into
          processing state. Reviewers who change their mind cancel and
          nothing is sent. */}
      <Dialog
        open={pendingChange !== null}
        onClose={handleCancelPending}
        aria-labelledby="classification-confirm-title"
        aria-describedby="classification-confirm-description"
      >
        <DialogTitle id="classification-confirm-title">Reprocess this document?</DialogTitle>
        <DialogContent>
          <DialogContentText id="classification-confirm-description" sx={{ mb: 1 }}>
            {pendingChange?.value === null ? (
              <>
                Clearing the classification override will reprocess this document with the auto-classified value (
                <strong>{formatDocumentCategory(auto_classified ?? "")}</strong>).
              </>
            ) : (
              <>
                Changing the classification from <strong>{pendingChange?.fromLabel}</strong> to{" "}
                <strong>{formatDocumentCategory(pendingChange?.value ?? "")}</strong> will reprocess this document.
              </>
            )}
          </DialogContentText>
          <DialogContentText sx={{ fontSize: "0.875rem" }}>
            The document will move out of <em>Review Required</em> briefly while the worker re-runs classification,
            re-matches a template, and regenerates extracted records. The page will update automatically when
            reprocessing is done — usually 10-30 seconds.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancelPending} disabled={isSaving}>
            Cancel
          </Button>
          <Button onClick={handleConfirmPending} variant="contained" color="primary" disabled={isSaving} autoFocus>
            Reprocess
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};
